'use client';

import { useCallback, useMemo, type FC } from 'react';

import { useTranslations } from 'next-intl';

import { LINK_NAVIGATION_MODE } from '@/common/constants';
import { useConfigValue } from '@/components/providers/config-provider';
import { useIsCarouselUrl, useRegisterCarouselUrl } from '@/components/ui/carousel-urls-context';
import { useLinkLocation } from '@/components/ui/link-location-context';
import { useAnalytics, type LinkClickEventData } from '@/hooks/use-analytics';
import { useEnvironmentUrl } from '@/hooks/use-environment-url';
import { useIframeNavigation, type ResolvedLinkLocation } from '@/hooks/use-iframe-navigation';
import { cn } from '@/lib/utils';
import { isValidHttpUrl } from '@/lib/utils/domain';

type MarkdownLinkProps = {
  className?: string;
  href?: string;
  children?: React.ReactNode;
  source?: LinkClickEventData['source'];
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

export const MarkdownLink: FC<MarkdownLinkProps> = ({
  className,
  href,
  children,
  source,
  target,
  onClick: userOnClick,
  ...props
}) => {
  // `target="_blank"` always opens in a new tab, regardless of rendering context
  // (e.g. external terms links), mirroring native anchor semantics.
  const forceNewTab = target === '_blank';
  const { getNavigationMode, navigate } = useIframeNavigation();
  const providedLocation = useLinkLocation();
  const baseUrl = useConfigValue<string>('navigation.baseUrl');
  const { processUrl } = useEnvironmentUrl();
  const { trackLinkClick } = useAnalytics();
  const t = useTranslations('markdown');

  const finalHref = useMemo(() => {
    if (!href) return null;

    try {
      const resolvedUrl = new URL(href, baseUrl ?? undefined).href;
      return processUrl(resolvedUrl);
    } catch {
      return processUrl(href);
    }
  }, [href, baseUrl, processUrl]);

  const isInsideCarousel = providedLocation !== null && providedLocation !== 'sidebar';
  useRegisterCarouselUrl(isInsideCarousel ? finalHref : null);
  // Only plain markdown links (no provider) need to subscribe to the registry;
  // carousel CTAs and the sidebar already know their location from context, so
  // skipping the subscription avoids re-renders when other carousel items mount.
  const lookupHref = providedLocation === null ? finalHref : null;
  const isRegisteredCarouselUrl = useIsCarouselUrl(lookupHref);

  const linkLocation: ResolvedLinkLocation = useMemo(() => {
    if (providedLocation) return providedLocation;
    if (isRegisteredCarouselUrl) return 'carousel';
    return 'markdown';
  }, [providedLocation, isRegisteredCarouselUrl]);

  const navigationMode = useMemo(
    () => (forceNewTab ? LINK_NAVIGATION_MODE.NEW_TAB : getNavigationMode(linkLocation)),
    [forceNewTab, getNavigationMode, linkLocation],
  );

  const analyticsSource: LinkClickEventData['source'] = source ?? linkLocation;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      userOnClick?.(e);
      if (e.defaultPrevented || !finalHref) return;

      if (e.metaKey || e.ctrlKey || e.shiftKey) {
        trackLinkClick({
          url: finalHref,
          linkText: e.currentTarget.textContent ?? undefined,
          navigationMode: 'new-tab',
          source: analyticsSource,
        });
        return;
      }

      e.preventDefault();
      trackLinkClick({
        url: finalHref,
        linkText: e.currentTarget.textContent ?? undefined,
        navigationMode,
        source: analyticsSource,
      });
      navigate({ href: finalHref, linkLocation, forceNewTab });
    },
    [
      userOnClick,
      finalHref,
      navigate,
      trackLinkClick,
      navigationMode,
      analyticsSource,
      linkLocation,
      forceNewTab,
    ],
  );

  const linkClasses = cn(
    'aui-md-a font-medium text-primary underline underline-offset-4 text-start',
    className,
  );

  const displayContent = useMemo(() => {
    const childrenText = children?.toString();
    return childrenText && isValidHttpUrl(childrenText) ? t('link') : children;
  }, [children, t]);

  const opensInNewTab = navigationMode === 'new-tab';

  return (
    <a
      className={linkClasses}
      href={finalHref ?? href}
      target={opensInNewTab ? '_blank' : undefined}
      rel={opensInNewTab ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      {...props}
    >
      {displayContent}
    </a>
  );
};
